export const PEER_SOURCE = 'peer_lens';

export const UNIT_SCOPE_STORAGE_KEY = 'ci-unit-scope';

const UNIT_METRIC_LABELS = {
  loan_growth_yoy: 'Loan growth (YoY)',
  deposit_growth_yoy: 'Deposit growth (YoY)',
  nie_growth_yoy: 'NIE growth ceiling (YoY)'
};

export const splitCompanyExpectations = (rows = []) => {
  const peer = [];
  const internal = [];
  rows.forEach((row) => {
    if (row.source === PEER_SOURCE) peer.push(row);
    else internal.push(row);
  });
  return { peer, internal };
};

export const formatExpectationUnit = (value, unit) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value ?? '');

  if (unit === 'ratio') {
    const pct = parsed * 100;
    return `${pct.toFixed(2)}%`;
  }

  if (unit === 'pct') {
    const sign = parsed > 0 ? '+' : '';
    return `${sign}${parsed.toFixed(1)}%`;
  }

  return String(parsed);
};

export const formatUnitMetricLabel = (metricKey) => {
  if (UNIT_METRIC_LABELS[metricKey]) return UNIT_METRIC_LABELS[metricKey];
  return String(metricKey || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatGrowthExpectationPct = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value ?? '');
  const sign = parsed > 0 ? '+' : '';
  return `${sign}${parsed.toFixed(1)}%`;
};

export const unitExpectationSourceLabel = (source) => {
  if (source === 'prior_forecast_seed') return 'Prior forecast default';
  if (source === 'manual') return 'Manual';
  return source || 'Source n/a';
};

export const filterActiveUnitRows = (rows = []) =>
  rows.filter((row) => {
    if (row.active === false) return false;
    if (row.metric_key === 'total_growth') return false;
    return true;
  });

export const collectUnitScopeOptions = (rows = []) => {
  const markets = [...new Set(rows.map((row) => row.market).filter(Boolean))].sort();
  const lobs = [...new Set(rows.map((row) => row.lob).filter(Boolean))].sort();
  return { markets, lobs };
};

export const filterUnitRowsByScope = (rows = [], scope = {}) => {
  const market = scope.market || 'All';
  const lob = scope.lob || 'All';
  const search = String(scope.search || '').trim().toLowerCase();

  return rows.filter((row) => {
    if (market !== 'All' && row.market !== market) return false;
    if (lob !== 'All' && row.lob !== lob) return false;
    if (search) {
      const haystack = `${row.planning_entity || ''} ${row.market || ''} ${row.lob || ''}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
};

export const groupUnitRowsByEntity = (rows = []) => {
  const grouped = new Map();
  rows.forEach((row) => {
    const entityKey = row.planning_entity || 'unknown';
    if (!grouped.has(entityKey)) {
      grouped.set(entityKey, {
        planning_entity: row.planning_entity,
        market: row.market,
        lob: row.lob,
        metrics: []
      });
    }
    grouped.get(entityKey).metrics.push(row);
  });

  return Array.from(grouped.values())
    .map((entity) => ({
      ...entity,
      metrics: entity.metrics.slice().sort((a, b) =>
        formatUnitMetricLabel(a.metric_key).localeCompare(formatUnitMetricLabel(b.metric_key))
      )
    }))
    .sort((a, b) => {
      const marketCmp = (a.market || '').localeCompare(b.market || '');
      if (marketCmp !== 0) return marketCmp;
      const lobCmp = (a.lob || '').localeCompare(b.lob || '');
      if (lobCmp !== 0) return lobCmp;
      return (a.planning_entity || '').localeCompare(b.planning_entity || '');
    });
};

export const unitRowId = (row) => row.id || `${row.planning_entity}-${row.metric_key}`;

export const isLowerIsBetter = (note) => {
  if (!note) return false;
  return String(note).toLowerCase().includes('lower is better');
};

export const EXPECTATIONS_VIEWS = {
  peer: 'peer',
  internal: 'internal'
};

// Peer benchmarks are a mocked v1 stub. Keep them out of the primary path until
// Peer Lens is backed by live data; opt in explicitly with the build-time flag.
export const isPeerContextEnabled = () =>
  typeof process !== 'undefined' &&
  !!process.env &&
  process.env.REACT_APP_CLOSE_INTEL_PEER_CONTEXT === '1';

export const parseExpectationsView = (search) => {
  const params = new URLSearchParams(search || '');
  const view = params.get('view');
  // Default to company guardrails; only honor the peer view when it is enabled.
  if (view === EXPECTATIONS_VIEWS.peer && isPeerContextEnabled()) return EXPECTATIONS_VIEWS.peer;
  return EXPECTATIONS_VIEWS.internal;
};
